<template>
    <div dir="rtl" class="mr-8 px-4 sm:px-6 lg:px-8">
        <div class="sm:flex sm:items-center">
            <div class="sm:flex-auto">
                <h1 class="text-xl font-semibold text-gray-900">ارسال التقارير للحلقات</h1>
                <p class="mt-2 text-sm text-gray-700">اختر الجلسة وارفع الملف المطلوب</p>
            </div>
        </div>

        <!-- Loading State -->
        <div v-if="isLoading" class="mt-4 flex justify-center">
            <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-customHafadhColor"></div>
        </div>

        <!-- Error Alert -->
        <div v-if="errorMessage" class="mt-4 bg-red-50 border border-red-200 rounded-md p-4">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="mr-3">
                    <p class="text-sm text-red-800">{{ errorMessage }}</p>
                </div>
                <div class="mr-auto pr-0">
                    <button @click="errorMessage = ''" class="text-red-400 hover:text-red-600">
                        <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <!-- Success Alert -->
        <div v-if="successMessage" class="mt-4 bg-green-50 border border-green-200 rounded-md p-4">
            <div class="flex">
                <div class="flex-shrink-0">
                    <svg class="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                </div>
                <div class="mr-3">
                    <p class="text-sm text-green-800">{{ successMessage }}</p>
                </div>
                <div class="mr-auto pr-0">
                    <button @click="successMessage = ''" class="text-green-400 hover:text-green-600">
                        <svg class="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                            <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
                        </svg>
                    </button>
                </div>
            </div>
        </div>

        <div class="mt-2 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-7">
            <select 
                @change="changeSessionsId(masterSessionValue)" 
                id="session-select" 
                name="session-select"
                autocomplete="off"
                class="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm pr-7"
                v-model="masterSessionValue"
                :disabled="isLoading">
                <option disabled value="" selected>الرجاء اختيار الجلسة</option>
                <option v-for="master in masterSessionsList" :key="master.id" :value="master.id">
                    {{ master.sessionsName }}
                </option>
            </select>
        </div>

        <div class="mt-3 col-span-6">
            <label class="block text-sm font-medium text-gray-700">
                رفع الملف
            </label>
            <div class="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-md hover:border-gray-400 transition-colors">
                <div class="space-y-1 text-center">
                    <svg class="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48"
                        aria-hidden="true">
                        <path
                            d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H12a4 4 0 01-4-4v-4m32-4l-3.172-3.172a4 4 0 00-5.656 0L28 28M8 32l9.172-9.172a4 4 0 015.656 0L28 28m0 0l4 4m4-24h8m-4-4v8m-12 4h.02"
                            stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    <div class="flex text-sm text-gray-600">
                        <label for="file-upload"
                            class="relative cursor-pointer bg-white rounded-md font-medium text-blue-600 hover:text-blue-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-blue-500">
                            <span>اختر الملف</span>
                            <input 
                                id="file-upload" 
                                name="file-upload" 
                                type="file" 
                                class="sr-only"
                                @change="handleFileUpload" 
                                accept=".png,.jpg,.jpeg,.pdf,.xlsx"
                                :disabled="isLoading" />
                        </label>
                        <p class="pr-1">الصيغات المقبوله لرفع الملف</p>
                    </div>
                    <p class="text-xs text-gray-500">
                        PNG, JPG, PDF, XLSX حتى 10 ميجابايت
                    </p>
                    <div v-if="fileName" class="mt-2 text-sm text-green-600">
                        <p>تم رفع الملف: {{ fileName }}</p>
                        <p class="text-xs text-gray-500">الحجم: {{ fileSize }}</p>
                    </div>
                </div>
            </div>
        </div>

        <div dir="ltr" class="mt-10"> 
            <button 
                class="button" 
                type="submit" 
                @click="sendReport"
                :disabled="!isFormValid || isSubmitting">
                <span v-if="isSubmitting" class="flex items-center">
                    <svg class="animate-spin -mr-1 ml-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    جاري الارسال...
                </span>
                <span v-else>ارسال</span>
            </button>
        </div>
    </div>
</template>

<script>
import url from '@/url';
import axios from 'axios';
import { defineComponent } from 'vue';

class MasterSessionDto {
    constructor(data) {
        this.id = data.id || null;
        this.sessionsId = data.sessionsId || null;
        this.sessionsName = data.sessionsName || null;
    }
}

export default defineComponent({
    data() {
        return {
            masterSessionValue: "",
            masterSessionsList: [],
            sessionsId: "",
            base64File: "",
            fileType: "",
            fileName: "",
            fileSize: "",
            isLoading: false,
            isSubmitting: false,
            errorMessage: "",
            successMessage: "",
            maxFileSize: 10 * 1024 * 1024, // 10MB in bytes
            allowedFileTypes: ['png', 'jpg', 'jpeg', 'pdf', 'xlsx']
        }
    },
    computed: {
        isFormValid() {
            return this.masterSessionValue && this.base64File && this.fileName;
        }
    },
    mounted() {
        this.getMasterSessions();
    },
    methods: {
        async getMasterSessions() {
            this.isLoading = true;
            this.errorMessage = "";
            
            try {
                const response = await axios.get(process.env.VUE_APP_SERVICE_URL + url.master_sessions, {
                    headers: {
                        'X-Authorization': localStorage.getItem('token'),
                    }
                });
                
                this.masterSessionsList = response.data.result.data.map(data => new MasterSessionDto(data));
                
                if (this.masterSessionsList.length > 0) {
                    this.masterSessionValue = this.masterSessionsList[0].id;
                    this.sessionsId = this.masterSessionsList[0].sessionsId;
                }
            } catch (error) {
                console.error('Error fetching master sessions:', error);
                this.errorMessage = 'حدث خطأ في تحميل الجلسات. يرجى المحاولة مرة أخرى.';
            } finally {
                this.isLoading = false;
            }
        },

        changeSessionsId(masterSessionValue) {
            const selectedSession = this.masterSessionsList.find(session => session.id === masterSessionValue);
            if (selectedSession) {
                this.sessionsId = selectedSession.sessionsId;
                console.log('Selected session ID:', this.sessionsId);
            }
        },

        validateFile(file) {
            // Check file size
            if (file.size > this.maxFileSize) {
                throw new Error(`حجم الملف كبير جداً. الحد الأقصى هو 10 ميجابايت.`);
            }

            // Check file type
            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (!this.allowedFileTypes.includes(fileExtension)) {
                throw new Error(`نوع الملف غير مدعوم. الأنواع المدعومة: ${this.allowedFileTypes.join(', ')}`);
            }

            return true;
        },

        formatFileSize(bytes) {
            if (bytes === 0) return '0 Bytes';
            const k = 1024;
            const sizes = ['Bytes', 'KB', 'MB', 'GB'];
            const i = Math.floor(Math.log(bytes) / Math.log(k));
            return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
        },

        handleFileUpload(event) {
            const file = event.target.files[0];
            if (!file) return;

            try {
                this.validateFile(file);
                this.convertFileToBase64(file);
            } catch (error) {
                this.errorMessage = error.message;
                // Reset file input
                event.target.value = '';
            }
        },

        convertFileToBase64(file) {
            const reader = new FileReader();
            
            reader.onload = (e) => {
                try {
                    this.fileType = file.type.split("/")[1] || file.name.split('.').pop();
                    this.base64File = e.target.result.split(",")[1];
                    this.fileName = file.name;
                    this.fileSize = this.formatFileSize(file.size);
                    
                    console.log('File processed successfully:', {
                        type: this.fileType,
                        name: this.fileName,
                        size: this.fileSize
                    });
                } catch (error) {
                    console.error('Error processing file:', error);
                    this.errorMessage = 'حدث خطأ في معالجة الملف. يرجى المحاولة مرة أخرى.';
                }
            };

            reader.onerror = () => {
                this.errorMessage = 'حدث خطأ في قراءة الملف. يرجى المحاولة مرة أخرى.';
            };

            reader.readAsDataURL(file);
        },

        async sendReport() {
            if (!this.isFormValid) {
                this.errorMessage = 'يرجى اختيار الجلسة ورفع ملف صحيح.';
                return;
            }

            this.isSubmitting = true;
            this.errorMessage = "";
            this.successMessage = "";

            try {
                const response = await axios.post(
                    process.env.VUE_APP_SERVICE_URL + url.send_chat,
                    {
                        message: "",
                        fileInStr: this.base64File,
                        fileType: this.fileType.toUpperCase(),
                        sessionId: this.sessionsId,
                        fileName: this.fileName,
                    },
                    {
                        headers: {
                            'X-Authorization': localStorage.getItem('token'),
                        }
                    }
                );

                console.log('Report sent successfully:', response);
                this.successMessage = 'تم إرسال التقرير بنجاح!';
                
                // Reset form
                this.resetForm();
                
            } catch (error) {
                console.error('Error sending report:', error);
                this.errorMessage = 'حدث خطأ في إرسال التقرير. يرجى المحاولة مرة أخرى.';
            } finally {
                this.isSubmitting = false;
            }
        },

        resetForm() {
            this.base64File = "";
            this.fileType = "";
            this.fileName = "";
            this.fileSize = "";
            
            // Reset file input
            const fileInput = document.getElementById('file-upload');
            if (fileInput) {
                fileInput.value = '';
            }
        }
    }
})
</script>

<style scoped>
.button {
    @apply px-4 py-2 bg-customHafadhColor text-white rounded hover:bg-customHafadhColor w-40 h-10 transition-colors;
}

.button:disabled {
    @apply opacity-50 cursor-not-allowed;
}

.button:not(:disabled):hover {
    @apply bg-opacity-90;
}
</style>
